import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import * as Sentry from '@sentry/angular-ivy';
import { AppToastComponent, AppToastData, AppToastVariant } from './app-toast.component';
import { CapturedHttpError, takeRecentHttpError } from './http-error-capture.interceptor';

/*
 * Aviso flutuante do canto inferior direito, portado do Aura Exploração
 * (aura-exploracao-frontend/src/app/shared/app-toast). Diferenças daqui:
 *  - quase toda tela do Inova abre o snackbar com `panelClass` ('mat-warn',
 *    'mat-success'...): essas classes viram a variante do cartão em vez de
 *    manter a faixa antiga;
 *  - textos em português/espanhol, seguindo o idioma ativo do Transloco.
 */

const ERROR_PATTERN = /fail|error|erro|falh|invalid|inv[aá]lid|not found|unable|could not|cannot|n[aã]o foi|n[aã]o [eé] poss[ií]vel|no se pudo/i;
const SUCCESS_PATTERN = /success|sucesso|[eé]xito|updated|saved|deleted|removed|created|applied|atualizad|salv|exclu[ií]d|criad|conclu[ií]d|enviad|guardad/i;

/** "Failed", "Error!", "Erro" — avisos que não dizem o que aconteceu. */
const GENERIC_ERROR = /^\s*(failed|error|erro|falha|falhou)\s*[.!]*\s*$/i;

/** Ação que só fecha o aviso: o X já faz isso, então o botão não aparece. */
const CLOSE_ACTION = /^\s*(fechar|close|cerrar|ok|x|dismiss)\s*$/i;

const DEFAULT_DURATION = 4000;

/** `panelClass` usados nas telas do Inova -> variante do cartão. */
const PANEL_VARIANT: Record<string, AppToastVariant> = {
	'mat-warn': 'error',
	'error-snackbar': 'error',
	'snack-error': 'error',
	'snackbar-error': 'error',
	'warning-snackbar': 'warning',
	'mat-success': 'success',
	'success-snackbar': 'success',
	'snack-success': 'success',
	'snackbar-success': 'success',
	'mat-primary': 'info',
	'info-snackbar': 'info',
};

type Lang = 'br' | 'es';

const TEXTS: Record<Lang, Record<string, string>> = {
	br: {
		success: 'Sucesso',
		info: 'Aviso',
		warning: 'Atenção',
		error: 'Erro',
		errorGeneric: 'Não foi possível concluir',
		confirm: 'Entendi',
		close: 'Fechar',
		support: 'Ocorreu um erro interno. Fale com o suporte e informe a referência {ref}.',
		retry: 'Não foi possível concluir a ação. Tente de novo; se continuar, fale com o suporte.',
	},
	es: {
		success: 'Éxito',
		info: 'Aviso',
		warning: 'Atención',
		error: 'Error',
		errorGeneric: 'No se pudo completar',
		confirm: 'Entendido',
		close: 'Cerrar',
		support: 'Ocurrió un error interno. Contacte al soporte e informe la referencia {ref}.',
		retry: 'No se pudo completar la acción. Intente de nuevo; si continúa, contacte al soporte.',
	},
};

const STATUS_TEXT: Record<number, string> = {
	0: 'sem conexão',
	400: 'dados inválidos',
	401: 'sessão expirada',
	403: 'sem permissão',
	404: 'não encontrado',
	409: 'conflito',
	413: 'muito grande',
	422: 'dados inválidos',
	500: 'erro no servidor',
	502: 'servidor indisponível',
	503: 'servidor indisponível',
	504: 'tempo esgotado',
};

let activeLang: () => string = () => 'br';

function texts(): Record<string, string> {
	return TEXTS[activeLang() === 'es' ? 'es' : 'br'];
}

function variantOf(message: string, config?: MatSnackBarConfig): AppToastVariant {
	const fromPanel = panelClasses(config).map((c) => PANEL_VARIANT[c]).find(Boolean);
	if (fromPanel) return fromPanel;
	if (ERROR_PATTERN.test(message)) return 'error';
	if (SUCCESS_PATTERN.test(message)) return 'success';
	return 'info';
}

function shortUrl(url: string): string {
	try {
		const { pathname } = new URL(url, window.location.origin);
		return pathname.replace(/^.*?\/api\//, '/');
	} catch {
		return url;
	}
}

function describe(error: CapturedHttpError): string {
	const status = error.status ? `${error.status} ${STATUS_TEXT[error.status] ?? ''}`.trim() : STATUS_TEXT[0];
	return `${error.method} ${shortUrl(error.url)} · ${status}`;
}

/** Monta título, mensagem e detalhe do aviso a partir do que a tela pediu. */
export function buildToastData(message: string, action?: string, config?: MatSnackBarConfig): AppToastData {
	const t = texts();
	const text = (message ?? '').toString().trim();
	const variant = variantOf(text, config);
	const hasAction = !!action;
	const actionLabel = action && !CLOSE_ACTION.test(action) ? action : null;

	if (variant !== 'error') {
		return {
			variant,
			title: t[variant],
			message: text,
			actionLabel,
			hasAction,
			closeLabel: t.close,
		};
	}

	// Erro: o motivo real vem da última resposta da API, quando ela acabou de falhar.
	const http = takeRecentHttpError();
	const raw = http?.message && !/^ocorreu um erro inesperado\.?$/i.test(http.message) ? http.message : null;
	const sensitive = !!raw && isSensitive(raw);
	const reference = http && (sensitive || http.status >= 500 || !raw) ? newReference() : null;
	if (http && reference) {
		// Detalhe completo só no log (console + Sentry), com a mesma referência do aviso.
		reportToLog(reference, http, text);
	}
	const backend = sensitive ? null : raw;
	const generic = GENERIC_ERROR.test(text);
	const supportHint = reference ? t.support.replace('{ref}', reference) : t.retry;

	let body: string;
	if (generic || !text) {
		body = backend ?? supportHint;
	} else {
		body = backend && !text.includes(backend) ? `${text}\n${backend}` : text;
		if (!backend && reference) body = `${text}\n${supportHint}`;
	}

	const detailParts = [http ? describe(http) : null, reference ? `ref ${reference}` : null].filter(Boolean);

	return {
		variant,
		title: generic || !text ? t.errorGeneric : t.error,
		message: body,
		detail: detailParts.length ? detailParts.join(' · ') : null,
		actionLabel: actionLabel ?? t.confirm,
		hasAction,
		closeLabel: t.close,
	};
}

/**
 * Mensagem que expõe o interior do sistema: SQL, Prisma, stack trace, caminho de
 * arquivo, conexão, credencial. Essa não vai para a tela; vai para o log.
 */
const SENSITIVE_PATTERN = new RegExp(
	[
		'prisma', 'invocation', '\\bsql\\b', 'select\\s.+\\sfrom', 'insert into', 'update\\s.+\\sset', 'constraint',
		'relation ".+"', 'column ".+"', 'syntax error', 'ECONN', 'ETIMEDOUT', 'ENOTFOUND', 'socket',
		'\\bat\\s+\\S+\\s+\\(', 'node_modules', '/usr/app', '\\.ts:\\d+', '\\.js:\\d+', 'stack',
		'password', 'passwd', 'secret', 'token', 'bearer', 'authorization', 'connection string',
		'postgres', 'redis', 'camunda', 'engine-rest', 'keycloak', 'TypeError', 'ReferenceError',
		'Cannot read propert', 'undefined', 'null value', 'ENGINE-\\d+',
	].join('|'),
	'i',
);

function isSensitive(message: string): boolean {
	return message.length > 300 || SENSITIVE_PATTERN.test(message);
}

/** Código curto para a pessoa passar ao suporte e alguém achar o erro no log. */
function newReference(): string {
	const time = Date.now().toString(36).slice(-5).toUpperCase();
	const rand = Math.random().toString(36).slice(2, 5).toUpperCase();
	return `ERR-${time}${rand}`;
}

function reportToLog(reference: string, http: CapturedHttpError, screenMessage: string): void {
	const context = { reference, screenMessage, method: http.method, url: http.url, status: http.status, backendMessage: http.message };
	console.error(`[app-toast] ${reference}`, context);
	try {
		Sentry.captureMessage(`HTTP ${http.status} ${http.method} ${shortUrl(http.url)} (${reference})`, {
			level: 'error',
			tags: { reference },
			extra: context,
		});
	} catch {
		// Sem Sentry configurado o console basta.
	}
}

function panelClasses(config?: MatSnackBarConfig): string[] {
	return [config?.panelClass].flat().filter(Boolean) as string[];
}

/**
 * Aviso com `panelClass` que não é de cor (ex.: 'export-wizard-dialog',
 * 'lithology-snackbar') mantém o visual próprio pelo `open` original.
 */
function hasOwnStyle(config?: MatSnackBarConfig): boolean {
	const classes = panelClasses(config);
	return classes.length > 0 && !classes.some((c) => c in PANEL_VARIANT);
}

let installed = false;

/**
 * Todo `snackBar.open(...)` passa a abrir o AppToastComponent no canto inferior
 * direito, em vez da faixa no meio da tela.
 *
 * Vai no protótipo porque o MatSnackBar do Angular Material 14 é fornecido por
 * módulo (`providedIn: MatSnackBarModule`): trocar o provider na raiz não chega
 * às telas lazy, e editar as ~160 telas uma a uma não é viável.
 *
 * Erro fica na tela até a pessoa confirmar ou fechar; os demais somem sozinhos.
 *
 * @param getLang idioma ativo do Transloco ('br' | 'es'), lido a cada aviso.
 */
export function installAppToast(getLang?: () => string): void {
	if (getLang) activeLang = getLang;
	if (installed) return;
	installed = true;

	const originalOpen = MatSnackBar.prototype.open;

	MatSnackBar.prototype.open = function (
		this: MatSnackBar,
		message: string,
		action = '',
		config?: MatSnackBarConfig,
	): MatSnackBarRef<TextOnlySnackBar> {
		if (hasOwnStyle(config)) {
			return originalOpen.call(this, message, action, config);
		}
		const data = buildToastData(message, action, config);
		const ref = this.openFromComponent(AppToastComponent, {
			...config,
			data,
			duration: data.variant === 'error' ? undefined : (config?.duration ?? DEFAULT_DURATION),
			horizontalPosition: 'end',
			verticalPosition: 'bottom',
			panelClass: ['app-toast-panel'],
			politeness: data.variant === 'error' ? 'assertive' : 'polite',
		});
		return ref as unknown as MatSnackBarRef<TextOnlySnackBar>;
	};
}
